import { Route, Routes } from "react-router-dom";
import { Suspense, lazy } from "react";

// Layouts
import { Layout, AuthLayout } from "../layouts";
import { DashboardLayout } from "../pages/dashboard-pages/layout";
import { ProtectedOtpRoute, PublicRoute, PrivateRoute, DashboardRoute } from "../components";

// Direct import for HomePage
import { HomePage } from "../pages";

// Lazy-loaded pages
const ProductPage = lazy(() => import("../pages/Products"));
const ProductDetailPage = lazy(() => import("../pages/Products/ProductDetailPage"));
const ServicePage = lazy(() => import("../pages/Services"));
const ResourcesPage = lazy(() => import("../pages/Resources/ResourcesPage"));
const ExamplesPage = lazy(() => import("../pages/Example"));
const ShoppingCart = lazy(() => import("../pages/shopping-cart"));
const CheckoutPage = lazy(() => import("../pages/Checkout"));
const ContactUs = lazy(() => import("../pages/contact-us"));
const PrivacyPolicy = lazy(() => import("../pages/privacy-policy"));
const TermsConditions = lazy(() => import("../pages/terms-conditions"));
const AboutUs = lazy(() => import("../pages/about-us"));
const Services = lazy(() => import("../pages/Services/services"));

// Auth Pages
const LoginPage = lazy(() => import("../pages/Auth").then(module => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import("../pages/Auth").then(module => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("../pages/Auth").then(module => ({ default: module.ForgotPasswordPage })));
const RegisterOtp = lazy(() => import("../pages/Auth").then(module => ({ default: module.RegisterOtp })));
const ResetPasswordPage = lazy(() => import("../pages/Auth").then(module => ({ default: module.ResetPasswordPage })));

// Admin Dashboard Pages
const DashboardHomePage = lazy(() => import("../pages/dashboard-pages/home/HomePage"));
const AdminHomePage = lazy(() => import("../pages/dashboard-pages/home/AdminHomePage"));
const OrdersPage = lazy(() => import("../pages/dashboard-pages/orders/OrdersPage"));
const SavedCart = lazy(() => import("../pages/dashboard-pages/saved-carts/SavedCart"));
const Profile = lazy(() => import("../pages/dashboard-pages/profile/Profile"));
const SettingsPage = lazy(() => import("../pages/dashboard-pages/settings/SettingsPage"));
const QuoteForm = lazy(() => import("../pages/dashboard-pages/quote/QuoteForm"));
const EnclosureBuilder = lazy(() => import("../pages/dashboard-pages/enclosure-builder/EnclosureBuilder"));
const ViewDesigns = lazy(() => import("../pages/dashboard-pages/enclosure-builder/ViewDesigns"));
const ProductsPage = lazy(() => import("../pages/dashboard-pages/products/ProductsPage"));
const AdminProductDetailPage = lazy(() => import("../pages/dashboard-pages/products/ProductDetailPage"));
const FolderPage = lazy(() => import("../pages/dashboard-pages/folder/FolderPage"));
const ViewTemplate = lazy(() => import("../pages/dashboard-pages/view-templates/ViewTemplates") )

// Admin Order Management Pages
const AdminOrdersPage = lazy(() => import("../pages/dashboard-pages/admin-orders").then(module => ({ default: module.AdminOrdersPage })));
const EditOrderPage = lazy(() => import("../pages/dashboard-pages/admin-orders").then(module => ({ default: module.EditOrderPage })));
const ViewOrderPage = lazy(() => import("../pages/dashboard-pages/admin-orders").then(module => ({ default: module.ViewOrderPage })));

// User Management Pages
const UsersPage = lazy(() => import("../pages/dashboard-pages/users"));
const UserForm = lazy(() => import("../pages/dashboard-pages/users/UserForm"));
const UserDetailsPage = lazy(() => import("../pages/dashboard-pages/users/UserDetailsPage"));

// Custom Quote Pages
import CustomQuoteLayout from '../pages/dashboard-pages/custom-quote/CustomQuoteLayout';
import NewRequest from '../pages/dashboard-pages/custom-quote/NewRequest';
import ViewRequests from '../pages/dashboard-pages/custom-quote/ViewRequests';

// Error Page
const ErrorPage = lazy(() => import("../pages/Error"));
import LoadingSpinner from '../components/LoadingSpinner';


const EnclosureManagementPage = lazy(() => import("../pages/dashboard-pages/enclosure-management/EnclosureManagementPage"));
const CustomQuoteManagement = lazy(() => import("../pages/dashboard-pages/custom-quote-management"));
const ViewQuotePage = lazy(() => import("../pages/dashboard-pages/custom-quote-management/ViewQuotePage"));

const AppRoutes = () => {
  return (
    <Suspense
      fallback={
        <div className="flex-1 min-h-screen flex items-center justify-center"><LoadingSpinner message="Loading..." /></div>
      }>
      <Routes>
        {/* Public Website Routes - Accessible even when server is offline */}
        <Route path="/" element={<Layout />}>
          {/* Home Route - Public */}
          <Route index element={<HomePage />} />

          {/* Product Routes - Public */}
          <Route path="products">
            <Route index element={<ProductPage />} />
            <Route path=":productId" element={<ProductDetailPage />} />
            <Route path="*" element={<ErrorPage />} />
          </Route>

          {/* Service Route - Public */}
          <Route path="services">
            <Route index element={<ServicePage />} />
            <Route path="/services/:service" element={<Services />} />
          </Route>

          {/* Resources Route - Public */}
          <Route path="resources" element={<ResourcesPage />} />

          {/* Examples Route - Public */}
          <Route path="examples" element={<ExamplesPage />} />

          {/* Contact Us Route - Public */}
          <Route path="contact-us" element={<ContactUs />} />

          {/* Privacy Policy Route - Public */}
          <Route path="privacy-policy" element={<PrivacyPolicy />} />

          {/* Terms and Conditions Route - Public */}
          <Route path="terms-and-conditions" element={<TermsConditions />} />

          {/* About Us Route - Public */}
          <Route path="about-us" element={<AboutUs />} />

          {/* Catch all route for any other invalid paths */}
          <Route path="*" element={<ErrorPage />} />



             <Route element={<PrivateRoute />}>
          {/* Shopping Cart Route - Protected */}
          <Route path="cart" element={<PrivateRoute showToast={true} />}>
            <Route index element={<ShoppingCart />} />
          </Route>

          {/* Checkout Route - Protected */}
          <Route path="checkout" element={<PrivateRoute showToast={true} />}>
            <Route index element={<CheckoutPage />} />
          </Route>
        </Route>
        </Route>

        {/* Protected Routes - Require Authentication */}
     

        {/* Dashboard Routes - Protected with Authentication */}
        <Route element={<DashboardRoute />}>
          <Route path="dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardHomePage />} />
            <Route path="home" element={<DashboardHomePage />} />
            <Route path="admin-home" element={<AdminHomePage />} />
            <Route path="orders">
              <Route index element={<OrdersPage />} />
              <Route path="view/:orderId" element={<ViewOrderPage />} />
            </Route>
            <Route path="saved-carts" element={<SavedCart />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="quote-form" element={<QuoteForm />} />

            {/* Custom Quote Routes */}
            <Route path="custom-quote" element={<CustomQuoteLayout />}>
              <Route path="new" element={<NewRequest />} />
              <Route path="requests" element={<ViewRequests />} />
            </Route>

            {/* Enclosure Builder Routes */}
            <Route path="enclosure-builder">
              <Route index element={<EnclosureBuilder />} />
              <Route path="create" element={<EnclosureBuilder />} />
              <Route path="designs" element={<ViewDesigns />} />
            </Route>

            <Route path="view-templates">
             
              <Route index  element={<ViewTemplate />} />
            </Route>
            

            {/* User Management Routes */}
            <Route path="users">
              <Route index element={<UsersPage />} />
              <Route path="add" element={<UserForm />} />
              <Route path="edit/:id" element={<UserForm />} />
              <Route path="view/:id" element={<UserDetailsPage />} />
            </Route>

            {/* Products Route */}
            <Route path="products">
              <Route index element={<ProductsPage />} />
              <Route path="view/:productId" element={<AdminProductDetailPage />} />
            </Route>

            {/* Folder Routes */}
            <Route path="folder/:folderId" element={<FolderPage />} />

            {/* Admin Order Management Routes */}
            <Route path="admin">
              <Route path="orders">
                <Route index element={<AdminOrdersPage />} />
                <Route path="view/:orderId" element={<ViewOrderPage />} />
                <Route path="edit/:orderId" element={<EditOrderPage />} />
              </Route>
            </Route>

            {/* Enclosure Management Route */}
            <Route path="enclosure-management" element={<EnclosureManagementPage />} />

            {/* Custom Quote Management Route */}
            <Route path="custom-quote-management">
              <Route index element={<CustomQuoteManagement />} />
              <Route path="view/:quoteId" element={<ViewQuotePage />} />
            </Route>

            {/* Add more dashboard routes as needed */}
          </Route>
        </Route>

        {/* Auth Routes - Public (no authentication required) */}
        <Route path="auth" element={<AuthLayout />}>
          <Route index element={<ErrorPage />} />

          {/* Wrap login, register, and forgot-password routes with PublicRoute */}
          <Route element={<PublicRoute />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/* Protected OTP Route - can't be accessed directly */}
          <Route element={<ProtectedOtpRoute />}>
            <Route path="verify-otp" element={<RegisterOtp />} />
          </Route>

          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<ErrorPage />} />
        </Route>

      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
